'use client';

import { type PointerEvent, useCallback, useRef, useState } from 'react';

import { cn } from '@/app/utils/cn';

const SWIPE_THRESHOLD_PX = 50;

// `grid-cols-1` caps the column at the container width; an auto column would grow to the
// slide's min-content (e.g. min-height × aspect-ratio) and overflow.
export const FADE_CAROUSEL_LIST_CLASS_NAME = 'grid grid-cols-1 touch-pan-y';

export const getFadeCarouselSlideClassName = (isSelected: boolean) =>
  cn(
    'col-start-1 row-start-1 min-w-0 transition-opacity duration-700 ease-in-out motion-reduce:transition-none',
    isSelected ? 'z-10 opacity-100' : 'pointer-events-none opacity-0'
  );

// Stacks slides in one grid cell and crossfades by opacity; swipe via pointer events.
export const useFadeCarousel = (total: number) => {
  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const swipeStartXRef = useRef<number | null>(null);

  const goTo = useCallback(
    (index: number) => {
      if (total === 0) return;
      setSelectedIndex(((index % total) + total) % total);
    },
    [total]
  );

  const goToNext = useCallback(() => {
    if (total === 0) return;
    setSelectedIndex((index) => (index + 1) % total);
  }, [total]);

  const goToPrev = useCallback(() => {
    if (total === 0) return;
    setSelectedIndex((index) => (index - 1 + total) % total);
  }, [total]);

  const onPageChange = useCallback((page: number) => goTo(page - 1), [goTo]);

  const onPointerDown = useCallback((event: PointerEvent) => {
    swipeStartXRef.current = event.clientX;
  }, []);

  const onPointerUp = useCallback(
    (event: PointerEvent) => {
      const startX = swipeStartXRef.current;
      swipeStartXRef.current = null;
      if (startX === null) return;

      const deltaX = event.clientX - startX;
      if (Math.abs(deltaX) < SWIPE_THRESHOLD_PX) return;

      if (deltaX < 0) {
        goToNext();
        return;
      }

      goToPrev();
    },
    [goToNext, goToPrev]
  );

  const onPointerCancel = useCallback(() => {
    swipeStartXRef.current = null;
  }, []);

  return {
    selectedIndex,
    currentPage: selectedIndex + 1,
    onPageChange,
    goToNext,
    swipeHandlers: { onPointerDown, onPointerUp, onPointerCancel },
  };
};
